import React, { useCallback, CSSProperties } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog, openDialog } from '../../../../../app/dialog/dialog.actions';
import {
  cancelSubscription,
  reactivateSubscription,
  startBilling,
} from '../../../../../app/page/subscription/subscription.actions';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import { paymentMethodSelectors } from '../../../../../app/resource/payment-method/payment-method.selectors';
import { subscriptionSelectors } from '../../../../../app/resource/subscription/subscription.selectors';
import { IPaymentMethod } from '../../../../../app/resource/payment-method/payment-method.types';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { useApiRequestState } from '../../../../../utils/api.utils';
import { formatCentsAsCurrency } from '../../../../../utils/currency.utils';
import { formatUtcDateToUserTimeZone } from '../../../../../utils/date.utils';
import { useSelectWithParams } from '../../../../../utils/selector.utils';
import { VacantSeats } from './VacantSeats';

const formatStatus = (status: string) => (status ? status.charAt(0).toUpperCase() + status.slice(1) : status);

const formatCancelledStatus = (accessEndsAt: string | null) => {
  if (accessEndsAt) {
    return `Cancelled (access until ${accessEndsAt})`;
  }

  return 'Cancelled';
};

const renderActionButtonContent = (isLoading: boolean, label: string, loaderStyle?: CSSProperties) => (
  <>
    {isLoading && <span className="css-loader mr-1" style={{ height: 20, width: 20, ...loaderStyle }} />}
    <span>{label}</span>
  </>
);

export const CurrentPlan = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const subscription = useSelector(subscriptionSelectors.selectCurrentSubscription);
  const paymentMethods = useSelector(paymentMethodSelectors.selectResourceList) as IPaymentMethod[];
  const loggedUserId = useSelector(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');
  const userTimezone = loggedUser?.timezone ?? null;
  const nextBillingDate = formatUtcDateToUserTimeZone(subscription?.nextBillingAt, userTimezone);
  const nextPaymentAttempt = formatUtcDateToUserTimeZone(subscription?.nextPaymentAttempt, userTimezone);

  const isTrialing = subscription?.status === 'trialing';
  const isCancelled = subscription?.cancelAt != null;
  const isScheduledToCancel = isCancelled && subscription?.status === 'active';
  const isIncomplete = subscription?.billingStatus === 'incomplete';
  const accessEndsAt = formatUtcDateToUserTimeZone(
    subscription?.cancelAt ?? subscription?.accessEndsAt ?? subscription?.currentPeriodEnd,
    userTimezone,
  );
  const renewalDate = formatUtcDateToUserTimeZone(
    subscription?.currentPeriodEnd ?? subscription?.nextBillingAt,
    userTimezone,
  );
  const canCancel =
    !isScheduledToCancel && !isIncomplete && (subscription?.status === 'trialing' || subscription?.status === 'active');
  const canReactivate = isScheduledToCancel && !isIncomplete;
  const canResubscribe = !isIncomplete && (subscription?.status === 'expired' || subscription?.status === 'canceled');
  const hasPaymentMethod = paymentMethods.length > 0;
  const hasPaymentIssue = Boolean(subscription?.hasPaymentIssue);
  const seatSyncPending = Boolean(subscription?.seatSyncPending);
  const pricePerUserLabel =
    subscription?.pricePerUser != null ? `${formatCentsAsCurrency(subscription.pricePerUser)} / user / month` : '—';
  const estimatedNextInvoiceLabel =
    subscription?.estimatedNextInvoiceCents != null
      ? formatCentsAsCurrency(subscription.estimatedNextInvoiceCents)
      : '—';
  const activeUsers = subscription?.userCount ?? 0;
  const [isStartingBilling] = useApiRequestState('startBilling');
  const [isCancellingSubscription] = useApiRequestState('deleteSubscription');
  const [isReactivatingSubscription] = useApiRequestState('reactivateSubscription');

  const onStartBilling = useCallback(() => {
    if (isStartingBilling) {
      return;
    }
    if (hasPaymentMethod) {
      dispatch(startBilling());
      return;
    }
    dispatch(openDialog('addPaymentMethodDialog', { endTrialOnSave: true }));
  }, [dispatch, hasPaymentMethod, isStartingBilling]);

  const onRetryIncompletePayment = useCallback(() => {
    if (isStartingBilling) {
      return;
    }
    dispatch(startBilling());
  }, [dispatch, isStartingBilling]);

  const onCancelClick = useCallback(() => {
    if (isCancellingSubscription) {
      return;
    }
    dispatch(
      openDialog('confirmation', {
        title: isTrialing ? 'Cancel trial' : 'Cancel subscription',
        message: isTrialing
          ? 'Are you sure you want to cancel your trial? You will lose access immediately.'
          : 'Are you sure you want to cancel your subscription? You will keep access until the end of the current billing period.',
        confirmButton: isTrialing ? 'Cancel trial' : 'Cancel subscription',
        onConfirm: () => {
          dispatch(closeDialog('confirmation'));
          dispatch(cancelSubscription());
        },
      }),
    );
  }, [dispatch, isCancellingSubscription, isTrialing]);

  const onReactivateClick = useCallback(() => {
    if (isReactivatingSubscription) {
      return;
    }
    dispatch(
      openDialog('confirmation', {
        title: 'Keep subscription',
        message: renewalDate
          ? `Your subscription will continue and renew on ${renewalDate}.`
          : 'Your subscription will continue and renew at the end of the current billing period.',
        confirmButton: 'Keep subscription',
        onConfirm: () => {
          dispatch(closeDialog('confirmation'));
          dispatch(reactivateSubscription());
        },
      }),
    );
  }, [dispatch, isReactivatingSubscription, renewalDate]);

  return (
    <div className="col px-0">
      {subscription ? (
        <div className="field-container px-2 mb-5">
          <div className="panel theme-gradient-2 py-1 px-2 flex aic mb-3">
            <h2 className="f-14 white bold">CURRENT PLAN</h2>
          </div>
          <div className="panel">
            {seatSyncPending && (
              <div className="subscription-detail-row py-2">
                <div className="f-14 orange bold mb-1">Provider sync pending</div>
                <div className="f-1 blueGray">
                  Seat counts are saved locally. Billing provider sync will retry automatically.
                </div>
              </div>
            )}
            {hasPaymentIssue && (
              <div className="subscription-detail-row py-2">
                <div className="f-14 red bold mb-1">{isIncomplete ? 'Payment pending' : 'Payment issue'}</div>
                <div className="f-1 blueGray mb-2">
                  {isIncomplete ? (
                    <>
                      We couldn&apos;t complete the payment to activate your subscription. Update your payment method
                      and try again.
                    </>
                  ) : nextPaymentAttempt ? (
                    <>
                      We couldn&apos;t process your latest payment. Next automatic retry is scheduled for{' '}
                      {nextPaymentAttempt}.
                    </>
                  ) : (
                    <>We couldn&apos;t process your latest payment. Please update your payment method to keep access.</>
                  )}
                </div>
                <div className="flex aic">
                  {isIncomplete && hasPaymentMethod && (
                    <button
                      type="button"
                      className="btn theme-bg-color white mr-2"
                      disabled={isStartingBilling}
                      style={isStartingBilling ? { opacity: 0.6 } : undefined}
                      onClick={onRetryIncompletePayment}
                    >
                      {renderActionButtonContent(isStartingBilling, 'Try again')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn theme-bg-color white"
                    onClick={() => history.push('/settings/subscription/payment-methods')}
                  >
                    Update payment method
                  </button>
                </div>
              </div>
            )}
            <div className="subscription-detail-row flex aic jcsb py-2">
              <div>
                <div className="f-1 blueGray mb-1">Plan status</div>
                <div className="f-16 bold">
                  {isScheduledToCancel ? formatCancelledStatus(accessEndsAt) : formatStatus(subscription.status)}
                </div>
              </div>
              {isTrialing && (
                <div className="text-right">
                  <div className="f-1 blueGray mb-1">Remaining trial days</div>
                  <div className="f-16 bold">
                    {subscription.remainingTrialDays === null ? 'Unlimited' : subscription.remainingTrialDays}
                  </div>
                </div>
              )}
            </div>
            <div className="subscription-detail-row flex aic jcsb py-2">
              <div>
                <div className="f-1 blueGray mb-1">Plan</div>
                <div className="f-16 bold">{subscription.planName || subscription.code}</div>
              </div>
            </div>
            <div className="subscription-detail-row flex aic jcsb py-2">
              <div>
                <div className="f-1 blueGray mb-1">Price</div>
                <div className="f-16 bold">{pricePerUserLabel}</div>
              </div>
            </div>
            {(isTrialing || subscription.status === 'active') && (
              <div className="subscription-detail-row flex aic jcsb py-2">
                <div>
                  <div className="f-1 blueGray mb-1">{isTrialing ? 'Next payment' : 'Next invoice total'}</div>
                  <div className="f-16 bold">{estimatedNextInvoiceLabel}</div>
                </div>
              </div>
            )}
            <div className="subscription-detail-row flex aic jcsb py-2">
              <div>
                <div className="f-1 blueGray mb-1">Workspace name</div>
                <div className="f-16 bold">{subscription.workspaceName || '—'}</div>
              </div>
            </div>
            {isScheduledToCancel && accessEndsAt && (
              <div className="subscription-detail-row flex aic jcsb py-2">
                <div>
                  <div className="f-1 blueGray mb-1">Access ends on</div>
                  <div className="f-16 bold">{accessEndsAt}</div>
                </div>
              </div>
            )}
            {nextBillingDate && (
              <div className="subscription-detail-row flex aic jcsb py-2">
                <div>
                  <div className="f-1 blueGray mb-1">Next billing date</div>
                  <div className="f-16 bold">{nextBillingDate}</div>
                </div>
              </div>
            )}
            <div className="subscription-detail-row flex aic jcsb py-2">
              <div>
                <div className="f-1 blueGray mb-1">Users in workspace</div>
                <div className="f-16 bold">{activeUsers}</div>
              </div>
              <button type="button" className="btn darkGray-bg white" onClick={() => history.push('/directory')}>
                View Directory
              </button>
            </div>
            <VacantSeats billedSeats={subscription.billedSeats} activeUsers={activeUsers} />
          </div>
          <div className="flex aic jcsb mt-3">
            {canCancel && (
              <button
                type="button"
                className="btn btn-border white-bg red"
                disabled={isCancellingSubscription}
                style={isCancellingSubscription ? { opacity: 0.6 } : undefined}
                onClick={onCancelClick}
              >
                {renderActionButtonContent(
                  isCancellingSubscription,
                  isTrialing ? 'Cancel Trial' : 'Cancel subscription',
                  {
                    border: '2px solid #f5c6cb',
                    borderBottomColor: '#dc3545',
                  },
                )}
              </button>
            )}
            {isTrialing && (
              <button
                type="button"
                className="btn theme-bg-color white"
                disabled={isStartingBilling}
                style={isStartingBilling ? { opacity: 0.6 } : undefined}
                onClick={onStartBilling}
              >
                {renderActionButtonContent(isStartingBilling, 'Upgrade')}
              </button>
            )}
            {canReactivate && (
              <button
                type="button"
                className="btn theme-bg-color white"
                disabled={isReactivatingSubscription}
                style={isReactivatingSubscription ? { opacity: 0.6 } : undefined}
                onClick={onReactivateClick}
              >
                {renderActionButtonContent(isReactivatingSubscription, 'Keep subscription')}
              </button>
            )}
            {canResubscribe && (
              <button
                type="button"
                className="btn theme-bg-color white"
                disabled={isStartingBilling}
                style={isStartingBilling ? { opacity: 0.6 } : undefined}
                onClick={onStartBilling}
              >
                {renderActionButtonContent(isStartingBilling, 'Resubscribe')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="field-container px-2 mb-5">
          <div className="f-14 blueGray">No subscription found for this workspace.</div>
        </div>
      )}
    </div>
  );
};
